// The detonation recipe kit: the input every detonation look draws from, and
// the arithmetic it draws with (R2079, `ui-map-interface.md` §6.3).
//
// A DETONATION look is what a round draws where it goes off. The World theme
// field `detonation` names one of ten, the cards the owner picked from
// `archive/mockups/065-torpedo-arrivals.html`. `detonation-styles.ts` is the
// registry that answers a look id, and `detonation-recipes-*.ts` hold the
// draw code. This module is what all of them import.
//
// This is a leaf (`leaves.test.ts`). It names the sinks of a layer through a
// type-only clause, the way `reload-recipe-kit.ts` does. The fx layer owns
// the clock, the list of live detonations and the batches, and it hands them
// in as ONE reused context a detonation.
//
// **The edge is the splash.** The widest edge of every look lands on the
// splash radius of the round's kind and nothing of it goes past, and a round
// of splash 0 draws inside the mark ring (`[detonation.limits]
// mark_ring_px`). The cards clip to that circle. The batches have no clip,
// so the kit keeps every mark inside it instead: a card's own geometry ends
// on the radius, `line` cuts a stroke at the circle, and a recipe drops a
// small mark that would cross it (`inside`).
//
// **Nothing here allocates on the frame path once a look has drawn.** A
// gradient cell key is built the first time its stops appear and kept by a
// numeric hash, the random numbers come from one module generator that a
// recipe seeds (`seed`, `rnd`), and every layout is kept by a numeric key.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP CARD MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards draw in WORLD px with the impact at the origin, on a canvas
// scaled by the zoom `st.z`. The translation is fixed:
//
//   | the card writes                  | the recipe writes                   |
//   | -------------------------------- | ----------------------------------- |
//   | `t`, `p.R`, `p.power`, `p.kind`  | `c.t`, `c.R`, `c.power`, `c.form`   |
//   | `p.seed`                         | `c.seed`                            |
//   | `ACCENT`                         | `c.ink`, the firer's faction colour |
//   | `WHITE`, `FIRE`, `PLUME`, …      | `INK.white`, `INK.fire`, …          |
//   | `MARK`, `px(n)`                  | `markPx`, `px(c, n)`                |
//   | `mulberry32(s)` then `r()`       | `seed(s)` then `rnd()`              |
//   | `disc(c, …)`                     | `disc(c, …)`                        |
//   | `circle(c, …)`, a dash           | `circle(c, …)`, `dashed(c, …)`      |
//   | `c.arc(…); stroke`               | `arc(c, …)`                         |
//   | `line(c, …)`, `P(…); S(…)`       | `line(c, …)`, `polyline(c, …)`      |
//   | `glow(c, …)`, a radial gradient  | `glow3(c, …)`, `glow4(c, …)`        |
//   | `band(c, …)` of group B          | `band(c, …)`                        |
//   | `hexPath(…); Fl(…)`              | `hexFill(c, …)`                     |
//   | a shard or the boil, filled      | an atlas cell (`detonation-cells`)  |
//
// A gradient is the one thing a batch cannot make, so `glow3` and `glow4`
// bake the card's stops into an atlas cell (`detonationSpecFor`) and the
// quad sink scales it. The stops' alphas are kept as shares of the largest
// one, in steps of 1/32, and the largest one is the quad's alpha, so a
// gradient that fades as a whole bakes once.

import { DETONATION_LIMITS, DETONATION_LOOKS, DETONATION_SEEDS } from '../gen/presentation';
import type { DetonationRow } from '../gen/presentation';
import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from '../fast/layers/batches';
import { TorpedoKind } from '../gen/proto/aquaphobia';
import { TAU, clamp01, smooth } from './recipe-kit';

export type { DetonationRow };
export {
  TAU, clamp as cl, clamp01, inCubic, lerp, outCubic, smooth,
} from './recipe-kit';

/** The six kinds a detonation reads as: the four of round 065 and the two
 *  buff forms of round 131, `screen` and `surge` (R2079, R2842). A blast
 *  wears the heat colours, and the other five wear the firer's ink. */
export type DetonationForm = 'blast' | 'slow' | 'sweep' | 'repair' | 'screen' | 'surge';

/** The inks of the cards, the palette of `ui-map-interface.md` §2. */
export const INK = {
  white: DETONATION_LIMITS.white,
  fire: DETONATION_LIMITS.fire,
  amber: DETONATION_LIMITS.amber,
  hot: DETONATION_LIMITS.hot,
  plume: DETONATION_LIMITS.plume,
  foam: DETONATION_LIMITS.foam,
  steel: DETONATION_LIMITS.steel,
  water: DETONATION_LIMITS.water,
} as const;

/** The footprint of a splash-0 round, the card's `MARK`. */
export const markPx = DETONATION_LIMITS.markRingPx;

/** How long the layer keeps a detonation: the card's `LIFE`. Every look is
 *  gone by then. */
export const DET_LIFE_SEC = DETONATION_LIMITS.lifeSec;

/**
 * ONE DETONATION, as a recipe reads it. The layer reuses ONE of these
 * (R131).
 *
 * Every length is world px and every angle is radians, with +x east and +y
 * south. A recipe writes local points about the impact; the primitives add
 * `x`, `y`.
 */
export interface DetonationCtx {
  /** The camera scale: CSS px for one world px. It is the card's `st.z`. */
  z: number;
  /** The figure row of the look that draws. */
  row: DetonationRow;
  /** The engine's round-cap flag (`fast/layer.ts` `F_ROUND`), handed in so
   *  this leaf takes no engine import. */
  round: number;
  /** The impact, in world px. */
  x: number;
  y: number;
  /** WALL seconds since the round went off. */
  t: number;
  /** The splash of the round's kind in world px, the card's `p.R`. */
  R: number;
  /** The damage of the kind over `[detonation.limits] power_damage`, held
   *  to 1: the card's `p.power`, the weight. */
  power: number;
  /** The kind as the card reads it. */
  form: DetonationForm;
  /** The layout seed of the kind, the card's `p.seed`. */
  seed: number;
  /** The firer's faction colour: the card's `ACCENT`. */
  ink: number;
  /** The course of the round in radians: the bearing from its launch point
   *  to the impact, the card's `FWD` (R2842). NaN for a round that names no
   *  launch point; `fwd` then biases no side. */
  course: number;
  quads: QuadSink;
  segs: SegmentSink;
  rings: CircleSink;
  /** An atlas cell by key. */
  sprite(key: string): SpriteEntry;
}

/** ONE DETONATION LOOK: what it draws for one detonation in one frame. */
export type DetonationRecipe = (c: DetonationCtx) => void;

/** The figures of one look, typed by the row the generator wrote. */
export type DetonationFigs<K extends keyof typeof DETONATION_LOOKS> = (typeof DETONATION_LOOKS)[K]['fig'];

/** The figures of the look `id` off the row that draws, read by name, so a
 *  name the row does not carry is a type error. */
export function figs<K extends keyof typeof DETONATION_LOOKS>(c: DetonationCtx, _id: K): DetonationFigs<K> {
  return c.row.fig as DetonationFigs<K>;
}

// ---------------------------------------------------------------------------
// The round
// ---------------------------------------------------------------------------

/** What the layer knows of a round kind: the catalog's splash and damage. */
export interface DetonationRound {
  splash: number;
  damage: number;
}

/**
 * The kind of a round as the cards read it (R2079, R2783, R2840, R2842).
 * The Torpor slows the hulls and the rounds in its splash, so it reads as
 * `slow`. The Countermine clears sea mines with a wide, weak warhead, the
 * SWEEP sample of round 065, so it reads as `sweep`. The three own-side
 * rounds of the torpedo refresh (R2779) read a calm form and never
 * `blast`: the Wrasse repairs and reads `repair`, the Sepia lays a quiet
 * screen and reads `screen`, and the Sailfish gives a surge and reads
 * `surge`, the two forms of round 131. Every other live round does damage
 * and reads as `blast`.
 */
export function formOf(kind: number): DetonationForm {
  switch (kind) {
    case TorpedoKind.TORPEDO_KIND_DISRUPTOR: return 'slow';
    case TorpedoKind.TORPEDO_KIND_WRASSE: return 'repair';
    case TorpedoKind.TORPEDO_KIND_COUNTERMINE: return 'sweep';
    case TorpedoKind.TORPEDO_KIND_SEPIA: return 'screen';
    case TorpedoKind.TORPEDO_KIND_SAILFISH: return 'surge';
    default: return 'blast';
  }
}

/**
 * The slow ring of a detonation in world px (R2783): the Torpor slows
 * every hostile hull and every hostile round inside its splash (R2779
 * rule 2), so its ring runs out to the splash. 0 for every other kind. The
 * Harpoon and the Remora kill hostile rounds inside their `burst_px`, and
 * they draw no ring of their own: the splash look covers the reach, and a
 * ROUND_KILL marks each kill.
 */
export function slowRingPx(kind: number, splash: number): number {
  return kind === TorpedoKind.TORPEDO_KIND_DISRUPTOR ? Math.max(0, splash) : 0;
}

/** The weight of a round: its damage over the damage of full weight, the
 *  card's `p.power = min(1, dmg / 300)`. */
export function powerOf(damage: number): number {
  return Math.min(1, Math.max(0, damage) / DETONATION_LIMITS.powerDamage);
}

/** The pack id of a kind (`light` for TORPEDO_KIND_LIGHT), or ''. */
export function roundIdOf(kind: number): string {
  const name = TorpedoKind[kind] ?? '';
  return name.startsWith('TORPEDO_KIND_') ? name.slice('TORPEDO_KIND_'.length).toLowerCase() : '';
}

/** The layout seed of a kind: the card's own seed for the three rounds the
 *  card shows, and a seed of its own for every other kind. */
export function seedOf(kind: number): number {
  const seeds = DETONATION_SEEDS as Readonly<Record<string, number>>;
  return seeds[roundIdOf(kind)] ?? DETONATION_LIMITS.seedBase;
}

// ---------------------------------------------------------------------------
// The arithmetic of the page
// ---------------------------------------------------------------------------

// `clamp01`, `cl` (the kit's `clamp`), `lerp`, `smooth`, `inCubic` and
// `outCubic` are the shared kit's, passed on above.

/** 1 before `a`, 0 after `b`: group A's `fade`. */
export function fade(t: number, a: number, b: number): number {
  return 1 - smooth(a, b, t);
}

export function outExpo(u: number): number {
  const v = clamp01(u);
  return v >= 1 ? 1 : 1 - Math.pow(2, -10 * v);
}

/** A stroke width that stays `n` screen px at every zoom: the card's
 *  `px(n)`. */
export function px(c: DetonationCtx, n: number): number {
  return n / c.z;
}

/** The drawn radius: the splash, or the mark ring for a splash-0 round. */
export function reachOf(c: DetonationCtx): number {
  return c.R > 0 ? c.R : markPx;
}

/** True when a mark of radius `r` at a local point stays inside `R`, the
 *  card's clip. */
export function inside(x: number, y: number, r: number, R: number): boolean {
  return Math.hypot(x, y) + r <= R;
}

// ---------------------------------------------------------------------------
// The shared parts of the two buff forms of round 131
// ---------------------------------------------------------------------------
//
// `archive/mockups/131-torpedo-buff-forms.html` gives every look a `screen`
// form (the Sepia) and a `surge` form (the Sailfish), and four parts serve
// them all: `FWD`, `fwd`, `buffEnv` and `curtain` (R2842). The page's
// `annulus` is the kit's `band`.

/** 1 on the forward side of the impact, 0 astern: the card's `fwd(a)`. A
 *  round with no course biases no side and answers 0.5 at every angle. */
export function fwd(c: DetonationCtx, a: number): number {
  return Number.isNaN(c.course) ? 0.5 : 0.5 + 0.5 * Math.cos(a - c.course);
}

/** The life of a buff form: in over `inT`, out from `oa` to `ob`, and `ob`
 *  held 0.05 s under the life, the card's `buffEnv`. */
export function buffEnv(t: number, inT: number, oa: number, ob: number): number {
  return smooth(0, inT, t) * (1 - smooth(oa, Math.min(ob, DET_LIFE_SEC - BUFF_END_MARGIN_SEC), t));
}

/** How far under the life a buff form is gone. */
const BUFF_END_MARGIN_SEC = 0.05;

/**
 * One look with its two buff forms (R2842): `screen` draws the screen form,
 * `surge` the surge form, and `base` every other form. A look file states
 * the three draws once in its `RECIPES`, so no draw tests the form for the
 * other two. The answer is built once, when the look file evaluates.
 */
export function withBuffForms(
  base: DetonationRecipe, screen: DetonationRecipe, surge: DetonationRecipe,
): DetonationRecipe {
  return (c) => {
    if (c.form === 'screen') screen(c);
    else if (c.form === 'surge') surge(c);
    else base(c);
  };
}

/** The bubble curtain of the card: one bubble each `CURTAIN_GAP_PX` of the
 *  rim, at least `CURTAIN_MIN_COUNT`, in a band `CURTAIN_DEEP_PX` deep (or
 *  `CURTAIN_DEEP_SHARE` of the radius) that stands `CURTAIN_HEM_PX` inside
 *  it. A bubble rises `CURTAIN_RISE_PX` over one cycle of
 *  `1 / CURTAIN_RATE` seconds. */
const CURTAIN_GAP_PX = 3.2;
const CURTAIN_MIN_COUNT = 10;
const CURTAIN_DEEP_PX = 5;
const CURTAIN_DEEP_SHARE = 0.14;
const CURTAIN_HEM_PX = 1.8;
const CURTAIN_RISE_PX = 1.6;
const CURTAIN_RATE = 0.45;

/**
 * A bubble curtain (065 card 12), the card's `curtain`: fine hollow
 * bubbles in a band just inside `r`. Each bubble rises a little, goes and
 * comes back, so the band stands still as a whole. A bubble the card's
 * clip would cut at `r` is left out.
 */
export function curtain(c: DetonationCtx, r: number, t: number, s: number, a: number, scale: number): void {
  if (a <= 0.004 || r <= 2) return;
  seed(s);
  const n = Math.max(CURTAIN_MIN_COUNT, Math.round((TAU * r) / CURTAIN_GAP_PX));
  const deep = Math.min(CURTAIN_DEEP_PX, r * CURTAIN_DEEP_SHARE), w = px(c, 0.9);
  for (let i = 0; i < n; i++) {
    const an = ((i + rnd() * 0.8) / n) * TAU, sz = (0.5 + 0.6 * rnd()) * scale;
    const d0 = r - CURTAIN_HEM_PX - sz - rnd() * deep;
    const ph = (t * CURTAIN_RATE + rnd()) % 1, al = a * Math.sin(Math.PI * ph);
    if (al <= 0.01) continue;
    const x = Math.cos(an) * d0, y = Math.sin(an) * d0 - ph * CURTAIN_RISE_PX;
    if (!inside(x, y, sz + w / 2, r)) continue;
    circle(c, x, y, sz, INK.foam, al, w);
  }
}

// ---------------------------------------------------------------------------
// The generator
// ---------------------------------------------------------------------------
//
// The page's `mulberry32`, kept in one module state so a recipe that draws
// a fresh sequence every frame allocates no closure: `seed(s)` is the
// page's `const r = mulberry32(s)`, and `rnd()` is `r()`. Only one sequence
// runs at a time, which is how every card uses it. A layout builder that
// runs in the middle of a recipe's sequence takes `world.mulberry32`, the
// same sequence in a closure of its own.

let rngState = 0;

/** Starts the sequence of the page's `mulberry32(s)`. */
export function seed(s: number): void {
  rngState = s | 0;
}

/** The next number of the running sequence, in [0, 1). */
export function rnd(): number {
  rngState = (rngState + 0x6d2b79f5) | 0;
  let t = Math.imul(rngState ^ (rngState >>> 15), 1 | rngState);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/**
 * A layout, built once for a splash, a weight, a kind and a seed and kept by
 * a numeric key: the card's `once`. `tag` names the card, so two cards of
 * one round keep two layouts. `build` reads all it needs off the context, so
 * a recipe hands in a function of the module and a frame makes no closure.
 */
const layouts = new Map<number, unknown>();
export function once<T>(tag: number, c: DetonationCtx, build: (c: DetonationCtx) => T): T {
  const key = (((tag * 4096 + Math.round(c.R * 4)) * 1024 + Math.round(c.power * 1000)) * 8
    + FORM_INDEX[c.form]) * 65536 + (c.seed & 0xffff);
  let v = layouts.get(key) as T | undefined;
  if (v === undefined) { v = build(c); layouts.set(key, v); }
  return v;
}

const FORM_INDEX: Readonly<Record<DetonationForm, number>> = {
  blast: 0, slow: 1, sweep: 2, repair: 3, screen: 4, surge: 5,
};

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// Each one takes a LOCAL point about the impact, clamps its alpha to 0 to 1
// as the page's `rgba` does, and skips a mark that would draw nothing.

/** A filled circle: the page's `disc`. */
export function disc(c: DetonationCtx, x: number, y: number, r: number, ink: number, a: number): void {
  const v = clamp01(a);
  if (r <= 0 || v <= 0) return;
  c.rings.disc(c.x + x, c.y + y, r, ink, v);
}

/** A stroked circle of width `w` centred on `r`: the page's `circle`. */
export function circle(
  c: DetonationCtx, x: number, y: number, r: number, ink: number, a: number, w: number,
): void {
  const v = clamp01(a);
  if (r <= 0 || w <= 0 || v <= 0) return;
  c.rings.ring(c.x + x, c.y + y, r, w, ink, v);
}

/**
 * A dashed circle: `on` px of dash and `off` px of gap along the rim from
 * `start`, the page's `circle` with a dash. The canvas starts the pattern
 * at angle 0 and turns clockwise, and so does the circle program.
 */
export function dashed(
  c: DetonationCtx, x: number, y: number, r: number, ink: number, a: number, w: number,
  on: number, off: number, start = 0,
): void {
  const v = clamp01(a);
  if (r <= 0 || w <= 0 || on <= 0 || v <= 0) return;
  c.rings.dashed(c.x + x, c.y + y, r, w, (TAU * r) / (on + off), on / (on + off), ink, v, start, TAU);
}

/** A stroked arc from `a0` to `a1`, clockwise: the page's
 *  `c.arc(…, a0, a1); c.stroke()`. */
export function arc(
  c: DetonationCtx, x: number, y: number, r: number, a0: number, a1: number,
  ink: number, a: number, w: number,
): void {
  const v = clamp01(a);
  if (r <= 0 || w <= 0 || a1 <= a0 || v <= 0) return;
  c.rings.arc(c.x + x, c.y + y, r, w, a0, a1 - a0, ink, v);
}

/**
 * A straight stroke with round caps, the page's `line`, cut at the circle
 * of radius `clip` about the impact: the card's clip. A stroke wholly
 * outside draws nothing. `clip` 0 cuts nothing.
 */
export function line(
  c: DetonationCtx, x0: number, y0: number, x1: number, y1: number,
  ink: number, a: number, w: number, clip = 0,
): void {
  const v = clamp01(a);
  if (w <= 0 || v <= 0) return;
  if (clip > 0) {
    const dx = x1 - x0, dy = y1 - y0;
    const A = dx * dx + dy * dy, B = 2 * (x0 * dx + y0 * dy), C = x0 * x0 + y0 * y0 - clip * clip;
    if (A <= 1e-12) {
      if (C > 0) return;
    } else {
      const disc2 = B * B - 4 * A * C;
      if (disc2 <= 0) return;
      const sq = Math.sqrt(disc2);
      const u0 = Math.max(0, (-B - sq) / (2 * A)), u1 = Math.min(1, (-B + sq) / (2 * A));
      if (u1 <= u0) return;
      const nx0 = x0 + dx * u0, ny0 = y0 + dy * u0;
      x1 = x0 + dx * u1; y1 = y0 + dy * u1; x0 = nx0; y0 = ny0;
    }
  }
  c.segs.line(c.x + x0, c.y + y0, c.x + x1, c.y + y1, w, ink, v, c.round);
}

/** A closed or open polyline of `n` points out of two scratch arrays, with
 *  the round joins of the page's `S`. */
export function polyline(
  c: DetonationCtx, xs: ArrayLike<number>, ys: ArrayLike<number>, n: number, close: boolean,
  ink: number, a: number, w: number, clip = 0,
): void {
  for (let i = 1; i < n; i++) line(c, xs[i - 1], ys[i - 1], xs[i], ys[i], ink, a, w, clip);
  if (close && n > 2) line(c, xs[n - 1], ys[n - 1], xs[0], ys[0], ink, a, w, clip);
}

/** A pointy-topped hex outline of radius `r`, the page's `hexPath` stroked:
 *  six strokes from the corner at -30 degrees. */
export function hexOutline(
  c: DetonationCtx, x: number, y: number, r: number, ink: number, a: number, w: number,
): void {
  if (r <= 0) return;
  for (let k = 0; k < 6; k++) {
    const a0 = ((60 * k - 30) * Math.PI) / 180, a1 = ((60 * (k + 1) - 30) * Math.PI) / 180;
    line(c, x + Math.cos(a0) * r, y + Math.sin(a0) * r, x + Math.cos(a1) * r, y + Math.sin(a1) * r, ink, a, w);
  }
}

// ---------------------------------------------------------------------------
// The atlas cells
// ---------------------------------------------------------------------------

/** The side of a gradient cell, in world px at scale 1. A quad scales it to
 *  the radius a card names. */
export const DET_GLOW_CELL_PX = DETONATION_LIMITS.glowCellPx;

/** The radius of the hex cell, in world px at scale 1. */
export const DET_HEX_CELL_R = 16;

/** The atlas key of the one hex cell: a white pointy-topped hex of radius
 *  `DET_HEX_CELL_R`, which `hexFill` tints and scales. */
export const DET_HEX_KEY = 'det:hex';

/** The steps an alpha share of a gradient stop takes. `detonation-cells.ts`
 *  reads a share back through it. */
export const DET_ALPHA_STEPS = 32;

/** The atlas key of one gradient: `det:g:` and each stop as its offset in
 *  thousandths, its colour and its alpha share in steps. */
export function glowKey(offs: ArrayLike<number>, cols: ArrayLike<number>, shares: ArrayLike<number>, n: number): string {
  let s = 'det:g:';
  for (let i = 0; i < n; i++) {
    if (i > 0) s += '_';
    s += `${Math.round(offs[i] * 1000)}.${cols[i].toString(16).padStart(6, '0')}.${shares[i]}`;
  }
  return s;
}

const glowKeys = new Map<number, string>();
const gOff = new Float64Array(6);
const gCol = new Int32Array(6);
const gAl = new Float64Array(6);
const gShare = new Int32Array(6);

/** Two 26-bit hashes of the stops, folded into one number below 2^52. */
function glowHash(n: number): number {
  let h1 = 0x811c9dc5 | 0, h2 = 0x01000193 | 0;
  for (let i = 0; i < n; i++) {
    h1 = mix1(mix1(mix1(h1, Math.round(gOff[i] * 1000)), gCol[i]), gShare[i]);
    h2 = mix2(mix2(mix2(h2, Math.round(gOff[i] * 1000)), gCol[i]), gShare[i]);
  }
  return ((h1 >>> 6) * 67108864) + (h2 >>> 6);
}

function mix1(h: number, v: number): number {
  return Math.imul(h ^ v, 0x01000193);
}

function mix2(h: number, v: number): number {
  return Math.imul(h ^ ((v + 0x9e3779b9) | 0), 0x85ebca6b);
}

/** Draws the stops in the scratch as one cell of radius `rx` by `ry`. */
function glowN(c: DetonationCtx, x: number, y: number, rx: number, ry: number, n: number): void {
  if (rx <= 0 || ry <= 0) return;
  let top = 0;
  for (let i = 0; i < n; i++) if (gAl[i] > top) top = gAl[i];
  const a = clamp01(top);
  if (a <= 0) return;
  // Each stop is a share of the quad's alpha `a`, held to 1. A share of
  // `top` would dim the other stops whenever a stop rises above 1; at or
  // under 1, `a` equals `top` and the shares are the same.
  for (let i = 0; i < n; i++) {
    gShare[i] = Math.round(Math.min(1, Math.max(0, gAl[i]) / a) * DET_ALPHA_STEPS);
  }
  const h = glowHash(n);
  let key = glowKeys.get(h);
  if (key === undefined) {
    key = glowKey(gOff, gCol, gShare, n);
    glowKeys.set(h, key);
  }
  // The cell is a square box of the gradient's disc, so the quad is the
  // disc's own box and the white tint keeps the baked inks.
  const e = c.sprite(key);
  c.quads.quad(c.x + x, c.y + y, 0, 0, 2 * rx, 2 * ry, 0, 0, 0xffffff, a, e.u0, e.v0, e.u1, e.v1); // mask
}

/** A radial gradient disc of three stops: the page's `glow` with three. */
export function glow3(
  c: DetonationCtx, x: number, y: number, r: number,
  o0: number, c0: number, a0: number, o1: number, c1: number, a1: number,
  o2: number, c2: number, a2: number,
): void {
  gOff[0] = o0; gCol[0] = c0; gAl[0] = a0;
  gOff[1] = o1; gCol[1] = c1; gAl[1] = a1;
  gOff[2] = o2; gCol[2] = c2; gAl[2] = a2;
  glowN(c, x, y, r, r, 3);
}

/** A radial gradient disc of two stops, squashed to `ry` across. */
export function glow2(
  c: DetonationCtx, x: number, y: number, r: number,
  o0: number, c0: number, a0: number, o1: number, c1: number, a1: number,
  ry = r,
): void {
  gOff[0] = o0; gCol[0] = c0; gAl[0] = a0;
  gOff[1] = o1; gCol[1] = c1; gAl[1] = a1;
  glowN(c, x, y, r, ry, 2);
}

/** A radial gradient disc of four stops, squashed to `ry` across: the
 *  page's `glow` under a `scale(1, sq)`. */
export function glow4(
  c: DetonationCtx, x: number, y: number, r: number,
  o0: number, c0: number, a0: number, o1: number, c1: number, a1: number,
  o2: number, c2: number, a2: number, o3: number, c3: number, a3: number,
  ry = r,
): void {
  gOff[0] = o0; gCol[0] = c0; gAl[0] = a0;
  gOff[1] = o1; gCol[1] = c1; gAl[1] = a1;
  gOff[2] = o2; gCol[2] = c2; gAl[2] = a2;
  gOff[3] = o3; gCol[3] = c3; gAl[3] = a3;
  glowN(c, x, y, r, ry, 4);
}

/** The steps the inner edge of a `band` takes, as a share of its outer
 *  radius. */
const BAND_STEPS = 16;

/**
 * A soft annulus from `r0` to `r1`, alpha `a0` inside to `a1` outside, and
 * nothing inside `r0`: group B's `band`. The inner edge stands on a step of
 * 1/16 of the outer radius, so a band that thins bakes 16 cells at most.
 */
export function band(
  c: DetonationCtx, r0: number, r1: number, ink: number, a0: number, a1: number,
): void {
  const lo = Math.max(0, r0);
  if (r1 <= 0 || r1 <= lo + 0.01) return;
  const q = Math.round((lo / r1) * BAND_STEPS) / BAND_STEPS;
  if (q <= 0) {
    gOff[0] = 0; gCol[0] = ink; gAl[0] = a0;
    gOff[1] = 1; gCol[1] = ink; gAl[1] = a1;
    glowN(c, 0, 0, r1, r1, 2);
    return;
  }
  if (q >= 1) return;
  gOff[0] = 0; gCol[0] = ink; gAl[0] = 0;
  gOff[1] = q; gCol[1] = ink; gAl[1] = 0;
  gOff[2] = q; gCol[2] = ink; gAl[2] = a0;
  gOff[3] = 1; gCol[3] = ink; gAl[3] = a1;
  glowN(c, 0, 0, r1, r1, 4);
}

/** A filled pointy-topped hex of radius `r` at a local point: the page's
 *  `hexPath(…); Fl(…)`. */
export function hexFill(c: DetonationCtx, x: number, y: number, r: number, ink: number, a: number): void {
  const v = clamp01(a);
  if (r <= 0 || v <= 0) return;
  const e = c.sprite(DET_HEX_KEY);
  c.quads.sprite(c.x + x, c.y + y, 0, e, ink, v, r / DET_HEX_CELL_R, 0, true);
}

/**
 * One baked shape of a look, with its origin at `(x, y)` and scaled `s`
 * about it: a shard of card 04 about its centroid, or the boil of card 07
 * about the impact. The cell carries the shape at scale 1.
 */
export function shape(
  c: DetonationCtx, key: string, x: number, y: number, s: number, ink: number, a: number,
): void {
  const v = clamp01(a);
  if (s <= 0 || v <= 0) return;
  const e = c.sprite(key);
  c.quads.sprite(c.x + x, c.y + y, 0, e, ink, v, s, 0, false);
}

/**
 * A dotted circle: round dots of diameter `w` every `gap` px along the
 * rim, the pattern moved back `off` px: group B's `dots` (a dash of 0.001
 * with round caps). A dot is a disc, because a dash that short draws
 * nothing in the circle program.
 */
export function dots(
  c: DetonationCtx, r: number, ink: number, a: number, w: number, gap: number, off: number,
  from = 0, sweep = TAU,
): void {
  if (r <= 0 || a <= 0 || gap <= 0) return;
  const period = gap + 0.001, len = r * sweep;
  let s = (((-off) % period) + period) % period;
  for (; s <= len; s += period) {
    const th = from + s / r;
    disc(c, Math.cos(th) * r, Math.sin(th) * r, w / 2, ink, a);
  }
}
